import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createAddressPayload } from 'src/app/MyQuote/CreateQuote/Steps/Steps.helper';
import { consigneeFormKeys, shipperFormKeys } from 'src/app/MyQuote/Modals/quoteDetailModal.const';
import { CURRENCY, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { setWorkflowSteps } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { createMultiStopQuote, createQuote } from 'src/services/QuoteService';
import { formatDecimalNumber } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { ADD_QUOTE_KEYS } from '../addQuote.const';
import {
    addQuoteAllData,
    addQuoteCompletedSteps,
    addQuoteConsigneeDetails,
    addQuoteDimensionsDetails,
    addQuoteServiceType,
    addQuoteShipperDetails,
    addQuoteShipmentDetails,
    addQuoteStopCount,
    addQuoteStops,
    resetAddQuoteData,
    setAddQuoteCompletedStep,
    setAddQuoteData,
    setAddQuoteRevalidateStep,
    setAddQuoteStep,
    setIsCreateLoading,
} from '../AddQuote.slice';
import { findPastDateStep } from '../dateGuard.helpers';
import { buildMultiStopPayload, isStopStepKey, stopPositionFromKey, stopStepKey } from '../multiStop.helpers';

const convertCurrency = (value: number, fromCurrency: string, toCurrency: string, currencyRates: { cadRate: number; usdRate: number }): number => {
    if (!value || value === 0 || fromCurrency === toCurrency) {
        return formatDecimalNumber(value || 0, 2);
    }

    let convertedValue = 0;

    if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
        convertedValue = value * (currencyRates.usdRate || 0.75);
    } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
        convertedValue = value * (currencyRates.cadRate || 1);
    } else {
        convertedValue = value;
    }

    return formatDecimalNumber(convertedValue, 2);
};

export const useSaveNextStep = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const workFlowSteps = useAppSelector((state: RootState) => state.auth.workFlowSteps);
    const addQuoteData = useAppSelector(addQuoteAllData);
    const serviceType = useAppSelector(addQuoteServiceType);
    const shipperDetails = useAppSelector(addQuoteShipperDetails);
    const consigneeDetails = useAppSelector(addQuoteConsigneeDetails);
    const shipmentDetails = useAppSelector(addQuoteShipmentDetails);
    const dimensionsDetails = useAppSelector(addQuoteDimensionsDetails);
    const completedSteps = useAppSelector(addQuoteCompletedSteps);
    const stopCount = useAppSelector(addQuoteStopCount);
    const stops = useAppSelector(addQuoteStops);
    const timezone = useAppSelector((state: RootState) => state.basic.appearance?.timezone);

    // Required steps for LTL before the Dimensions submit can create a quote
    const LTL_REQUIRED_STEPS = [ADD_QUOTE_KEYS.SHIPPER_DETAILS, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS, ADD_QUOTE_KEYS.SHIPMENT_DETAILS];

    // Required steps for FTL before the combined step can create a quote
    const FTL_REQUIRED_STEPS = [ADD_QUOTE_KEYS.SHIPPER_DETAILS, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS];

    /**
     * Block a create whose pickup/delivery date has gone into the past, and send the user to the
     * step that owns it with that date field flagged red.
     *
     * No toast on purpose (client decision) — the red border plus the field's own inline message
     * on the step we land on already say what is wrong, and a toast on top of it just covers the
     * form.
     *
     * The *_REQUIRED_STEPS guards below only ask whether a step was COMPLETED, which Duplicate
     * Quote pre-marks for every step — so a duplicated quote carrying yesterday's pickup date
     * sailed past them straight into createQuote. Same hole for a wizard left open across
     * midnight.
     *
     * Deliberately does NOT clear the step's completed flag: the user is free to fix the date and
     * jump straight back to the final step from the sidebar, and an un-ticked step would make the
     * *_REQUIRED_STEPS guard bounce them a second time with no error shown — a dead-end loop.
     * revalidateStep is what makes the destination step re-run its own yup on mount, so the date
     * lands with a red border instead of a pristine form.
     *
     * Returns true when the caller must abort.
     */
    const blockOnPastDates = (): boolean => {
        const pastDateStep = findPastDateStep({ shipperDetails, consigneeDetails, timezone });

        if (!pastDateStep) return false;

        dispatch(setAddQuoteRevalidateStep(pastDateStep));
        dispatch(setAddQuoteStep(pastDateStep));

        return true;
    };

    const createApiPayload = async (data: any, dimensionsDetailsOverride?: any) => {
        const shipperAddress = createAddressPayload(shipperDetails?.shipperAddress, shipperFormKeys);
        const consigneeAddress = createAddressPayload(consigneeDetails?.consigneeAddress, consigneeFormKeys);
        const effectiveDimensionsDetails = dimensionsDetailsOverride ?? dimensionsDetails;
        const additionalServices = effectiveDimensionsDetails?.additionalServices;

        let currencyRates = { cadRate: 1, usdRate: 0.75 };

        try {
            const response = await getPricingRate();

            if (response?.data) {
                currencyRates = {
                    cadRate: response.data.cadRate || 1,
                    usdRate: response.data.usdRate || 0.75,
                };
            }
        } catch (error) {
            console.error('Error fetching currency rates:', error);
        }

        let insuranceCommercialValueCAD = 0;
        let insuranceCommercialValueUSD = 0;

        if (data?.insuranceCommercialValue && data?.insuranceCurrencyType) {
            const numericValue = parseFloat(String(data.insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const currentCurrency = data.insuranceCurrencyType || CURRENCY.CAD;

                insuranceCommercialValueCAD = currentCurrency === CURRENCY.CAD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.CAD, currencyRates);

                insuranceCommercialValueUSD = currentCurrency === CURRENCY.USD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.USD, currencyRates);
            }
        }

        const getDimensions = () => {
            if (!effectiveDimensionsDetails || effectiveDimensionsDetails?.dimensions?.length === 0 || serviceType === QUOTE_CLASSIFICATION.FTL) {
                return [];
            }

            if (effectiveDimensionsDetails?.dimensions?.length === 1) {
                const dimension = effectiveDimensionsDetails?.dimensions[0];
                const hasValidValue =
                    (dimension.weight && dimension.weight >= 1) ||
                    (dimension.itemLength && dimension.itemLength >= 1) ||
                    (dimension.width && dimension.width >= 1) ||
                    (dimension.height && dimension.height >= 1) ||
                    (dimension.totalWeight && dimension.totalWeight >= 1);

                if (!hasValidValue) {
                    return [];
                }
            }

            return effectiveDimensionsDetails?.dimensions.map((dimension: any) => ({
                id: dimension.id,
                handlingUnit: dimension.handlingUnit,
                handlingUnitNo: dimension.handlingUnitNo,
                weight: dimension.weight,
                itemLength: dimension.itemLength,
                width: dimension.width,
                height: dimension.height,
                totalWeight: dimension.totalWeight,
                isStack: dimension.isStack,
                weightMeasurement: effectiveDimensionsDetails.weightMeasurement,
                lengthMeasurement: effectiveDimensionsDetails.lengthMeasurement,
                description: dimension.description,
                freightClass: dimension.freightClass,
                commodity: dimension.commodity,
                itemClassForDayross: dimension.itemClassForDayross,
            }));
        };

        // Merge service IDs from all three steps: pickup (shipper), delivery (consignee), and other (dimensions/shipment)
        const pickupServiceIds: number[] = (shipperDetails?.serviceId || []).map(Number).filter(Boolean);
        const deliveryServiceIds: number[] = (consigneeDetails?.serviceId || []).map(Number).filter(Boolean);
        const otherServiceIds: number[] = (additionalServices?.serviceId || []).map(Number).filter(Boolean);
        const combined = [...pickupServiceIds, ...deliveryServiceIds, ...otherServiceIds];
        const allServiceIds = combined.filter((id, index) => combined.indexOf(id) === index);

        return {
            // Only present when the wizard was pre-filled by Duplicate Quote — the BE uses it to
            // write the "duplicated quote <code>" activity on the new quote and ignores it otherwise.
            duplicatedFromQuoteId: addQuoteData?.duplicatedFromQuoteId ?? null,
            address: {
                ...shipperAddress,
                ...consigneeAddress,
                shipperId: shipperDetails?.shipperId,
                consigneeId: consigneeDetails?.consigneeId,
                divisionId: addQuoteData?.divisionId,
                customerBillingAccountId: addQuoteData?.customerBillingAccountId,
                classification: serviceType,
                urgency: addQuoteData?.urgency,
                serviceLevel: addQuoteData?.serviceLevel ?? null,
                pickupDate: shipperDetails?.pickupDate,
                pickupEndDate: shipperDetails?.pickupEndDate,
                pickupWindowType: shipperDetails?.pickupWindowType,
                pickupStartTime: shipperDetails?.pickupStartTime,
                pickupEndTime: shipperDetails?.pickupEndTime,
                pickupSensitivity: shipperDetails?.pickupSensitivity,
                deliveryDate: consigneeDetails?.deliveryDate,
                deliveryEndDate: consigneeDetails?.deliveryEndDate,
                deliveryWindowType: consigneeDetails?.deliveryWindowType,
                deliveryStartTime: consigneeDetails?.deliveryStartTime,
                deliveryEndTime: consigneeDetails?.deliveryEndTime,
                deliverySensitivity: consigneeDetails?.deliverySensitivity,
                ...data,
                insuranceCommercialValue: insuranceCommercialValueCAD,
                insuranceCommercialValueUSD: insuranceCommercialValueUSD,
                linearFootage: effectiveDimensionsDetails?.linearFootage,
                totalWeight: effectiveDimensionsDetails?.totalWeight,
            },
            dimensions: getDimensions(),
            services: {
                serviceId: allServiceIds.join(','),
                // Limited access delivery is collected in ConsigneeDetails step (not Dimensions)
                limitedAccess: consigneeDetails?.limitedAccessDelivery ?? false,
                ...(consigneeDetails?.limitedAccessDelivery && {
                    limitedAccessType: consigneeDetails?.limitedAccessDeliveryType,
                    limitedAccessOther: consigneeDetails?.limitedAccessDeliveryOther,
                }),
                inbond: additionalServices?.inbond,
                ...(additionalServices?.inbond && {
                    inbondType: additionalServices?.inbondType,
                    inbondName: additionalServices?.inbondName,
                    inbondAddress: additionalServices?.inbondAddress,
                    inbondContactMethod: additionalServices?.inbondContactMethod,
                    inbondContactValue: additionalServices?.inbondContactValue,
                }),
                dangerousGoods: additionalServices?.dangerousGoods,
                ...(additionalServices?.dangerousGoods && {
                    dangerGoodsType: additionalServices?.dangerGoodsType,
                    unitedNationsNumber: additionalServices?.unitedNationsNumber,
                    packagingGroup: additionalServices?.packagingGroup,
                    goodsClass: additionalServices?.goodsClass,
                    description: additionalServices?.description,
                    emergencyContactName: additionalServices?.emergencyContactName,
                    emergencyContactNumber: additionalServices?.emergencyContactNumber,
                    emergencyContactExt: additionalServices?.emergencyContactExt,
                    emergencyEapnbr: additionalServices?.emergencyEapnbr,
                }),
            },
        };
    };

    const onSaveNext = useCallback(
        (data: any, currentStep: string, localCompletedSteps?: string[]) => {
            // Multi-stop route (stopCount > 2): middle stop steps ('stop_details_2', ...) save
            // into `stops[position]` and advance to the next stop, or to Consignee once the
            // last middle stop is done. Handled BEFORE the switch below so the legacy
            // stopCount===2 behavior in the switch is untouched.
            if (isStopStepKey(currentStep)) {
                const position = stopPositionFromKey(currentStep);

                dispatch(setAddQuoteData({ data: { stops: { ...stops, [position]: data } } }));
                dispatch(setAddQuoteCompletedStep(currentStep));

                const nextPosition = position + 1;

                dispatch(setAddQuoteStep(nextPosition <= stopCount - 1 ? stopStepKey(nextPosition) : ADD_QUOTE_KEYS.CONSIGNEE_DETAILS));

                return;
            }

            switch (currentStep) {
                case ADD_QUOTE_KEYS.SHIPPER_DETAILS: {
                    dispatch(setAddQuoteData({ data: { shipperDetails: data } }));
                    dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.SHIPPER_DETAILS));

                    // If the new pickup date is after the already-saved delivery date, the
                    // delivery date is no longer valid — clear it and mark step 2 incomplete.
                    const newPickupDate = data.pickupDate;
                    const savedDeliveryDate = consigneeDetails?.deliveryDate;

                    if (newPickupDate && savedDeliveryDate && new Date(newPickupDate) > new Date(savedDeliveryDate)) {
                        dispatch(
                            setAddQuoteData({
                                data: {
                                    consigneeDetails: {
                                        ...consigneeDetails,
                                        deliveryDate: null,
                                        deliveryEndDate: null,
                                    },
                                },
                            })
                        );
                        dispatch(setAddQuoteCompletedStep(completedSteps.filter((s) => s !== ADD_QUOTE_KEYS.CONSIGNEE_DETAILS)));
                    }

                    // Multi-stop route: after Shipper (Stop 1), go to the first middle stop
                    // (stop_details_2) instead of straight to Consignee. At stopCount===2 this
                    // resolves to CONSIGNEE_DETAILS exactly as before.
                    dispatch(setAddQuoteStep(stopCount > 2 ? stopStepKey(2) : ADD_QUOTE_KEYS.CONSIGNEE_DETAILS));
                    break;
                }

                case ADD_QUOTE_KEYS.CONSIGNEE_DETAILS:
                    dispatch(setAddQuoteData({ data: { consigneeDetails: data } }));
                    dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.CONSIGNEE_DETAILS));
                    dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.SHIPMENT_DETAILS));
                    break;

                case ADD_QUOTE_KEYS.SHIPMENT_DETAILS:
                    // LTL: save and proceed to DIMENSIONS
                    dispatch(setAddQuoteData({ data: { shipmentDetails: data } }));
                    dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.SHIPMENT_DETAILS));
                    dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.DIMENSIONS));
                    break;

                case ADD_QUOTE_KEYS.DIMENSIONS: {
                    dispatch(setAddQuoteData({ data: { dimensionsDetails: data } }));

                    // Stale pickup/delivery date (duplicated quote, or wizard left open past
                    // midnight) — bounce to the owning step. The line items the user just entered
                    // are already saved above, so nothing is lost.
                    if (blockOnPastDates()) return;

                    if (localCompletedSteps) {
                        dispatch(setAddQuoteCompletedStep([...localCompletedSteps, ADD_QUOTE_KEYS.DIMENSIONS]));
                    } else {
                        dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.DIMENSIONS));
                    }

                    // Multi-stop route (Task 8): both LTL and FTL multileg quotes submit through
                    // THIS DIMENSIONS case (FTL multileg's Shipment Details step defers here — see
                    // onSaveFtlCombined below, which never creates when stopCount > 2). Runs BEFORE
                    // the LTL-only guard/create beneath it (that one assumes exactly the legacy
                    // [Shipper, Consignee, Shipment] required-steps list and the single-call
                    // createQuote()) and returns, so the stopCount===2 code beneath is untouched —
                    // neither classic LTL nor classic FTL ever reach this case with stopCount > 2.
                    if (stopCount > 2) {
                        const multiStopRequiredSteps = [
                            ADD_QUOTE_KEYS.SHIPPER_DETAILS,
                            ...Array.from({ length: stopCount - 2 }, (_, i) => stopStepKey(i + 2)),
                            ADD_QUOTE_KEYS.CONSIGNEE_DETAILS,
                            ADD_QUOTE_KEYS.SHIPMENT_DETAILS,
                        ];

                        for (const step of multiStopRequiredSteps) {
                            if (!completedSteps.includes(step)) {
                                dispatch(setAddQuoteStep(step));

                                return;
                            }
                        }

                        // Multi-stop submit (Task 8) — POST /quotes/multi-stop instead of the
                        // legacy address+dimensions+services single call. shipmentDetails (the
                        // ShipmentDetails/FtlLoadShipmentDetails slice value) carries
                        // primaryEquipmentType/secondary*/insurance* but NOT totalWeight/linearFootage —
                        // those live on THIS Dimensions submission's own `data` (mirrors
                        // createApiPayload's `effectiveDimensionsDetails?.totalWeight` pattern
                        // above), so merge them in here rather than relying on the stale
                        // shipmentDetails slice value.
                        dispatch(setIsCreateLoading(true));

                        // Insurance CAD/USD conversion (Task 8b — closes the gap found in Task 8's
                        // review): mirrors createApiPayload's getPricingRate()+convertCurrency()
                        // logic above EXACTLY, but is done here (in the hook) rather than inside
                        // buildMultiStopPayload so that helper stays a pure, synchronously-testable
                        // function — same layering classic uses (conversion lives in the hook,
                        // the payload builder just forwards already-converted values).
                        (async () => {
                            let currencyRates = { cadRate: 1, usdRate: 0.75 };

                            try {
                                const response = await getPricingRate();

                                if (response?.data) {
                                    currencyRates = {
                                        cadRate: response.data.cadRate || 1,
                                        usdRate: response.data.usdRate || 0.75,
                                    };
                                }
                            } catch (error) {
                                console.error('Error fetching currency rates:', error);
                            }

                            let insuranceCommercialValueCAD = 0;
                            let insuranceCommercialValueUSD = 0;

                            if (shipmentDetails?.insuranceCommercialValue && shipmentDetails?.insuranceCurrencyType) {
                                const numericValue = parseFloat(String(shipmentDetails.insuranceCommercialValue)) || 0;

                                if (numericValue > 0) {
                                    const currentCurrency = shipmentDetails.insuranceCurrencyType || CURRENCY.CAD;

                                    insuranceCommercialValueCAD =
                                        currentCurrency === CURRENCY.CAD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.CAD, currencyRates);
                                    insuranceCommercialValueUSD =
                                        currentCurrency === CURRENCY.USD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.USD, currencyRates);
                                }
                            }

                            const multiStopPayload = buildMultiStopPayload({
                                stopCount,
                                divisionId: addQuoteData?.divisionId ?? null,
                                customerBillingAccountId: addQuoteData?.customerBillingAccountId ?? null,
                                serviceType,
                                urgency: addQuoteData?.urgency ?? null,
                                serviceLevel: addQuoteData?.serviceLevel ?? null,
                                shipperDetails,
                                consigneeDetails,
                                stops,
                                shipmentDetails: {
                                    ...(shipmentDetails ?? {}),
                                    totalWeight: data?.totalWeight,
                                    linearFootage: data?.linearFootage,
                                    insuranceCommercialValue: insuranceCommercialValueCAD,
                                    insuranceCommercialValueUSD: insuranceCommercialValueUSD,
                                },
                                dimensions: data?.dimensions ?? [],
                                weightMeasurement: data?.weightMeasurement,
                                additionalServices: data?.additionalServices,
                            });

                            // Duplicate Quote source id rides alongside the builder's output rather
                            // than inside it, so buildMultiStopPayload stays a pure shape-of-the-quote
                            // builder (same reason the insurance conversion above lives here).
                            createMultiStopQuote({ ...multiStopPayload, duplicatedFromQuoteId: addQuoteData?.duplicatedFromQuoteId ?? null })
                                .then((res: any) => {
                                    const quoteId = res?.data?.quoteId;

                                    dispatch(setWorkflowSteps({ ...workFlowSteps, CREATE_FIRST_QUOTE: true }));
                                    WalToast.success(t('MyQuote.Quote_Created'));
                                    dispatch(resetAddQuoteData({ clearAll: true }));
                                    // No isNewQuote state: this is a manual-review quote (spec CP7 —
                                    // multi-stop never instant-prices), so the detail page should show
                                    // the submitted/manual-review presentation, not wait on a rating
                                    // socket that will never fire.
                                    navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true });
                                })
                                .catch((error: any) => {
                                    console.error('Error creating multi-stop quote:', error);
                                    WalToast.error(error?.response?.data?.message || t('MyQuote.Quote_Create_Failed'));
                                })
                                .finally(() => {
                                    dispatch(setIsCreateLoading(false));
                                });
                        })();

                        return;
                    }

                    // Guard: ensure all prior steps are complete before submitting
                    for (const step of LTL_REQUIRED_STEPS) {
                        if (!completedSteps.includes(step)) {
                            dispatch(setAddQuoteStep(step));

                            return;
                        }
                    }

                    dispatch(setIsCreateLoading(true));
                    createApiPayload(shipmentDetails ?? {}, data)
                        .then((payload) => {
                            createQuote(payload)
                                .then((response: any) => {
                                    if (response?.data) {
                                        WalToast.success('Quote created');
                                        dispatch(setWorkflowSteps({ ...workFlowSteps, CREATE_FIRST_QUOTE: true }));
                                        dispatch(resetAddQuoteData({ clearAll: true }));
                                        const quoteId = response.data.data.quote.id;
                                        navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true, state: { isNewQuote: true } });
                                    }
                                })
                                .catch((error: any) => {
                                    console.error('Error creating quote:', error);
                                    const errMsg = error?.response?.data?.message || 'Booking failed';
                                    WalToast.error(errMsg);
                                })
                                .finally(() => {
                                    dispatch(setIsCreateLoading(false));
                                });
                        })
                        .catch((error: any) => {
                            dispatch(setIsCreateLoading(false));
                            console.error('Error building quote payload:', error);
                        });
                    break;
                }
            }
        },
        [dispatch, completedSteps, navigate, workFlowSteps, addQuoteData, shipmentDetails, consigneeDetails, stopCount, stops, shipperDetails, serviceType, timezone, t]
    );

    // Persist-only path: writes the step's payload into the slice WITHOUT changing the active
    // step and WITHOUT running yup validation or marking the step complete. Used by sidebar
    // step-item clicks so the user's in-flight values survive jumping between steps.
    const onPersist = useCallback(
        (data: any, currentStep: string) => {
            if (isStopStepKey(currentStep)) {
                const position = stopPositionFromKey(currentStep);
                dispatch(setAddQuoteData({ data: { stops: { ...stops, [position]: data } } }));

                return;
            }

            switch (currentStep) {
                case ADD_QUOTE_KEYS.SHIPPER_DETAILS: {
                    dispatch(setAddQuoteData({ data: { shipperDetails: data } }));

                    // Mirror onSaveNext's cross-step rule: a pickup date later than the saved
                    // delivery date invalidates that delivery date. Persist must apply it too —
                    // without this, sidebar-jumping away from Shipper after moving the pickup
                    // date forward would leave Consignee still marked complete with a now-earlier
                    // delivery date, and the Dimensions submit guard would happily create it.
                    const newPickupDate = data?.pickupDate;
                    const savedDeliveryDate = consigneeDetails?.deliveryDate;

                    if (newPickupDate && savedDeliveryDate && new Date(newPickupDate) > new Date(savedDeliveryDate)) {
                        dispatch(
                            setAddQuoteData({
                                data: {
                                    consigneeDetails: {
                                        ...consigneeDetails,
                                        deliveryDate: null,
                                        deliveryEndDate: null,
                                    },
                                },
                            })
                        );
                        dispatch(setAddQuoteCompletedStep(completedSteps.filter((step) => step !== ADD_QUOTE_KEYS.CONSIGNEE_DETAILS)));
                    }

                    break;
                }
                case ADD_QUOTE_KEYS.CONSIGNEE_DETAILS:
                    dispatch(setAddQuoteData({ data: { consigneeDetails: data } }));
                    break;
                case ADD_QUOTE_KEYS.SHIPMENT_DETAILS:
                    dispatch(setAddQuoteData({ data: { shipmentDetails: data } }));
                    break;
                case ADD_QUOTE_KEYS.DIMENSIONS:
                    dispatch(setAddQuoteData({ data: { dimensionsDetails: data } }));
                    break;
            }
        },
        [dispatch, stops, consigneeDetails, completedSteps]
    );

    // Persist-only counterpart of onSaveFtlCombined — the classic FTL combined step feeds BOTH
    // dimensionsDetails and shipmentDetails from one form, so its sidebar persist writes both.
    const onPersistFtlCombined = useCallback(
        (dimensionsPayload: any, shipmentPayload: any) => {
            dispatch(setAddQuoteData({ data: { dimensionsDetails: dimensionsPayload } }));
            dispatch(setAddQuoteData({ data: { shipmentDetails: shipmentPayload } }));
        },
        [dispatch]
    );

    const onBack = useCallback(
        (data: any, currentStep: string) => {
            // Multi-stop route: back from a middle stop goes to the previous stop, or to
            // Shipper (Stop 1) when backing out of the first middle stop. Existing callers
            // (Consignee/Shipment/Dimensions) never pass a stop-step key, so this branch is
            // additive and never intercepts their calls.
            if (isStopStepKey(currentStep)) {
                const position = stopPositionFromKey(currentStep);

                dispatch(setAddQuoteData({ data: { stops: { ...stops, [position]: data } } }));
                dispatch(setAddQuoteStep(position <= 2 ? ADD_QUOTE_KEYS.SHIPPER_DETAILS : stopStepKey(position - 1)));

                return;
            }

            switch (currentStep) {
                case ADD_QUOTE_KEYS.CONSIGNEE_DETAILS:
                    dispatch(setAddQuoteData({ data: { consigneeDetails: data } }));
                    // Multi-stop route: back from Consignee goes to the last middle stop.
                    // At stopCount===2 this resolves to SHIPPER_DETAILS exactly as before.
                    dispatch(setAddQuoteStep(stopCount > 2 ? stopStepKey(stopCount - 1) : ADD_QUOTE_KEYS.SHIPPER_DETAILS));
                    break;

                case ADD_QUOTE_KEYS.SHIPMENT_DETAILS:
                    dispatch(setAddQuoteData({ data: { shipmentDetails: data } }));
                    dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.CONSIGNEE_DETAILS));
                    break;

                case ADD_QUOTE_KEYS.DIMENSIONS:
                    dispatch(setAddQuoteData({ data: { dimensionsDetails: data } }));
                    dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.SHIPMENT_DETAILS));
                    break;
            }
        },
        [dispatch, stopCount, stops]
    );

    // Used by FtlLoadShipmentDetails: saves dimensions + shipment data and creates the quote
    const onSaveFtlCombined = useCallback(
        async (dimensionsPayload: any, shipmentPayload: any) => {
            dispatch(setIsCreateLoading(true));

            // Multi-stop route (Task 8): FTL multileg's Shipment Details step (this combined
            // form) collects the same equipment/insurance/other-services fields as classic FTL,
            // but does NOT create the quote here — save shipmentDetails (equipment fields feed
            // buildMultiStopPayload's quote.primaryEquipmentType etc. in the DIMENSIONS branch
            // above) and advance to the real Dimensions step (ADD_QUOTE_STEPS's CP5 addition),
            // which requires real line items (the BE's multiStopDimensionSchema is min(1)
            // required regardless of classification) and creates the quote on submit. Runs
            // BEFORE marking DIMENSIONS complete/saving dimensionsPayload below — that combined
            // dimensionsPayload always carries `dimensions: []` for FTL (see
            // FtlLoadShipmentDetails onSubmit) and would otherwise falsely flag Dimensions done
            // and clobber the real line items entered on the next screen. Returns so the
            // stopCount===2 code beneath (classic FTL) never sees this branch (HARD GATE).
            if (stopCount > 2) {
                dispatch(setAddQuoteData({ data: { shipmentDetails: shipmentPayload } }));
                dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.SHIPMENT_DETAILS));

                for (const step of FTL_REQUIRED_STEPS) {
                    if (!completedSteps.includes(step)) {
                        dispatch(setAddQuoteStep(step));
                        dispatch(setIsCreateLoading(false));

                        return;
                    }
                }

                dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.DIMENSIONS));
                dispatch(setIsCreateLoading(false));

                return;
            }

            dispatch(setAddQuoteData({ data: { dimensionsDetails: dimensionsPayload } }));
            dispatch(setAddQuoteData({ data: { shipmentDetails: shipmentPayload } }));

            // Same stale-date guard as the LTL Dimensions submit — classic FTL creates the quote
            // from THIS combined step, so the check has to run here too.
            if (blockOnPastDates()) {
                dispatch(setIsCreateLoading(false));

                return;
            }

            dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.DIMENSIONS));
            dispatch(setAddQuoteCompletedStep(ADD_QUOTE_KEYS.SHIPMENT_DETAILS));

            // Guard: ensure all required prior steps are complete
            for (const step of FTL_REQUIRED_STEPS) {
                if (!completedSteps.includes(step)) {
                    dispatch(setAddQuoteStep(step));
                    dispatch(setIsCreateLoading(false));

                    return;
                }
            }

            createApiPayload(shipmentPayload, dimensionsPayload)
                .then((payload) => {
                    createQuote(payload)
                        .then((response: any) => {
                            if (response?.data) {
                                WalToast.success('Quote created');
                                dispatch(setWorkflowSteps({ ...workFlowSteps, CREATE_FIRST_QUOTE: true }));
                                dispatch(resetAddQuoteData({ clearAll: true }));
                                const quoteId = response.data.data.quote.id;
                                navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true, state: { isNewQuote: true } });
                            }
                        })
                        .catch((error: any) => {
                            console.error('Error creating quote:', error);
                            const errMsg = error?.response?.data?.message || 'Booking failed';
                            WalToast.error(errMsg);
                        })
                        .finally(() => {
                            dispatch(setIsCreateLoading(false));
                        });
                })
                .catch((error: any) => {
                    console.error('Error building quote payload:', error);
                    dispatch(setIsCreateLoading(false));
                });
        },
        // shipperDetails/consigneeDetails/timezone feed the stale-date guard above; without them the
        // callback would close over the dates as they were when the step first mounted.
        [dispatch, navigate, workFlowSteps, completedSteps, stopCount, shipperDetails, consigneeDetails, timezone, t]
    );

    const onBackFtlCombined = useCallback(
        (dimensionsPayload: any, shipmentPayload: any) => {
            dispatch(setAddQuoteData({ data: { dimensionsDetails: dimensionsPayload } }));
            dispatch(setAddQuoteData({ data: { shipmentDetails: shipmentPayload } }));
            dispatch(setAddQuoteStep(ADD_QUOTE_KEYS.CONSIGNEE_DETAILS));
        },
        [dispatch]
    );

    return { onSaveNext, onBack, onPersist, onPersistFtlCombined, createApiPayload, onSaveFtlCombined, onBackFtlCombined };
};
